import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TableContainer,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Alert,
  CircularProgress,
  InputAdornment,
  Divider,
  Stack,
  Chip,
} from '@mui/material';
import { format, parse, parseISO, isValid } from 'date-fns';
import { formatNumberWithCommas } from '../../../../utils/generalFunctions';
import { previewAmortizacion } from '../funciones/preview_amortizacion';
import MetodoDePagoDialog from '../../centro_cobros/components/metododepagodialog';
import { postAbonoCapital } from '../../../../utils/generalsFetch';

const toNumber = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  const s = typeof v === 'string' ? v.replaceAll(',', '') : v;
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

const parseFlexibleDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const s = String(value);
  const tryFormats = [
    () => parseISO(s),
    () => parse(s, 'dd-MM-yyyy', new Date()),
    () => parse(s, 'dd/MM/yyyy', new Date()),
    () => parse(s, 'yyyy-MM-dd', new Date()),
  ];
  for (const fn of tryFormats) {
    const d = fn();
    if (isValid(d)) return d;
  }
  return null;
};

const findPrimeraPendiente = (amortizacion) => {
  if (!Array.isArray(amortizacion)) return null;
  const pendientes = amortizacion.filter((c) =>
    String(c.estado || '').toLowerCase() !== 'pagado'
  );
  if (pendientes.length === 0) return null;
  return pendientes.reduce((min, c) => {
    if (!min) return c;
    return Number(c.numero_cuota) < Number(min.numero_cuota) ? c : min;
  }, null);
};

const getCuotaTarget = (cuota) => {
  if (!cuota) return 0;
  return toNumber(cuota.cuota ?? cuota.valor);
};

const supportsPreview = (metodo) => {
  const m = String(metodo || '').toLowerCase();
  return m === 'fija' || m === 'fijo' || m === 'flat' || m === 'insoluto';
};

const AbonoCapitalDialog = ({
  open,
  onClose,
  prestamo,
  prestamoDetalle,
  amortizacion,
  idcompany,
  usuarioId,
  idSucursal,
  idCliente,
  moneda = 'RD',
  atraso = 0,
  onSuccess,
}) => {
  const [monto, setMonto] = useState('');
  const [estrategia, setEstrategia] = useState('reducir_plazo');
  const [error, setError] = useState('');
  const [showMetodoDialog, setShowMetodoDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const capitalPendiente = useMemo(() => {
    const ms = toNumber(prestamoDetalle?.monto_solicitado);
    const cp = toNumber(prestamoDetalle?.capPagado);
    const direct = toNumber(prestamoDetalle?.capitalPendiente);
    if (direct > 0) return direct;
    return Math.max(ms - cp, 0);
  }, [prestamoDetalle]);

  const primeraPendiente = useMemo(
    () => findPrimeraPendiente(amortizacion),
    [amortizacion]
  );

  const cuotasPendientes = useMemo(() => {
    if (!Array.isArray(amortizacion)) return 0;
    return amortizacion.filter(
      (c) => String(c.estado || '').toLowerCase() !== 'pagado'
    ).length;
  }, [amortizacion]);

  const cuotaObjetivo = useMemo(
    () => getCuotaTarget(primeraPendiente),
    [primeraPendiente]
  );

  const fechaPrimeraCuota = useMemo(
    () => parseFlexibleDate(primeraPendiente?.fecha),
    [primeraPendiente]
  );

  const metodo = prestamoDetalle?.metodo;
  const frecuencia = prestamoDetalle?.frecuencia;

  const montoNum = toNumber(monto);
  const nuevoCapital = Math.max(capitalPendiente - montoNum, 0);
  const saldaPrestamo = montoNum > 0 && nuevoCapital === 0;

  const nPeriodosNuevos = useMemo(() => {
    if (!montoNum || montoNum <= 0) return null;
    if (saldaPrestamo) return 0;
    if (estrategia === 'reducir_cuota') return cuotasPendientes;
    // reducir_plazo: búsqueda numérica si el preview soporta el método
    if (!supportsPreview(metodo) || !fechaPrimeraCuota || cuotaObjetivo <= 0) {
      return null; // se calcula en backend
    }
    for (let n = 1; n <= cuotasPendientes; n++) {
      const cuotas = previewAmortizacion({
        metodo,
        montoSolicitado: nuevoCapital,
        interes: prestamoDetalle?.interes,
        plazo: n,
        frecuencia,
        firstCuotaDate: fechaPrimeraCuota,
      });
      if (!cuotas || cuotas.length === 0) continue;
      const noUltima = cuotas.slice(0, Math.max(cuotas.length - 1, 1));
      const maxCuota = Math.max(...noUltima.map((c) => toNumber(c.cuota)));
      if (maxCuota <= cuotaObjetivo + 0.5) return n;
    }
    return cuotasPendientes;
  }, [
    montoNum, saldaPrestamo, estrategia, cuotasPendientes, metodo,
    fechaPrimeraCuota, cuotaObjetivo, nuevoCapital, prestamoDetalle, frecuencia,
  ]);

  const previewNuevasCuotas = useMemo(() => {
    if (!montoNum || saldaPrestamo) return [];
    if (!supportsPreview(metodo) || !fechaPrimeraCuota || !nPeriodosNuevos) return [];
    return previewAmortizacion({
      metodo,
      montoSolicitado: nuevoCapital,
      interes: prestamoDetalle?.interes,
      plazo: nPeriodosNuevos,
      frecuencia,
      firstCuotaDate: fechaPrimeraCuota,
    });
  }, [
    montoNum, saldaPrestamo, metodo, fechaPrimeraCuota, nPeriodosNuevos,
    nuevoCapital, prestamoDetalle, frecuencia,
  ]);

  useEffect(() => {
    if (open) {
      setMonto('');
      setEstrategia('reducir_plazo');
      setError('');
      setSubmitting(false);
    }
  }, [open]);

  const validar = () => {
    if (!prestamoDetalle) return 'No hay préstamo seleccionado';
    if (String(metodo || '').toLowerCase() === 'plazo_indefinido')
      return 'Los préstamos indefinidos no admiten abono a capital';
    if (toNumber(atraso) > 0)
      return 'El préstamo tiene cuotas vencidas; debe estar al día';
    if (!montoNum || montoNum <= 0) return 'Ingrese un monto mayor a 0';
    if (montoNum > capitalPendiente)
      return `El monto excede el capital pendiente (${formatNumberWithCommas(capitalPendiente.toFixed(2))})`;
    if (cuotasPendientes <= 0) return 'No hay cuotas pendientes';
    return '';
  };

  const handleContinuar = () => {
    const msg = validar();
    if (msg) {
      setError(msg);
      return;
    }
    setError('');
    setShowMetodoDialog(true);
  };

  const handleConfirmMetodoPago = async (paymentData) => {
    setShowMetodoDialog(false);
    setSubmitting(true);
    try {
      const metodoPagoArr = paymentData?.combined
        ? (paymentData.payments || []).map((p) => ({
            method: p.method,
            amount: p.amount,
            reference: p.reference,
          }))
        : [{
            method: paymentData.method,
            amount: paymentData.amount,
            reference: paymentData.reference,
          }];

      const fechaPago = format(new Date(), "yyyy-MM-dd HH:mm:ss");

      const payload = {
        id_prestamo: prestamoDetalle.id,
        monto: montoNum,
        estrategia,
        id_usuario: usuarioId,
        id_sucursal: idSucursal,
        id_cliente: idCliente,
        fecha_pago: fechaPago,
        metodo_pago: metodoPagoArr,
      };

      const res = await postAbonoCapital({ idcompany, usuarioId, payload });
      if (onSuccess) onSuccess(res);
      onClose && onClose();
    } catch (e) {
      setError(e.message || 'Error al aplicar el abono');
    } finally {
      setSubmitting(false);
    }
  };

  const renderResumen = () => (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <Chip label={`Capital pendiente actual: ${moneda} ${formatNumberWithCommas(capitalPendiente.toFixed(2))}`} />
        <Chip label={`Cuotas pendientes: ${cuotasPendientes}`} />
        {cuotaObjetivo > 0 && (
          <Chip label={`Cuota actual: ${moneda} ${formatNumberWithCommas(cuotaObjetivo.toFixed(2))}`} />
        )}
        <Chip label={`Método: ${metodo || 'N/A'}`} />
        <Chip label={`Frecuencia: ${frecuencia || 'N/A'}`} />
      </Stack>
    </Box>
  );

  const renderPreview = () => {
    if (!montoNum || montoNum <= 0) return null;
    if (saldaPrestamo) {
      return (
        <Alert severity="success" sx={{ mt: 2 }}>
          Este abono saldará completamente el préstamo. Las cuotas pendientes serán eliminadas.
        </Alert>
      );
    }
    return (
      <Box mt={2}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Chip color="primary" label={`Nuevo capital: ${moneda} ${formatNumberWithCommas(nuevoCapital.toFixed(2))}`} />
          {nPeriodosNuevos != null && (
            <Chip color="primary" label={`Cuotas nuevas: ${nPeriodosNuevos}`} />
          )}
          {previewNuevasCuotas.length > 0 && (
            <Chip color="primary" label={`Cuota estimada: ${moneda} ${previewNuevasCuotas[0]?.cuota}`} />
          )}
        </Stack>
        {!supportsPreview(metodo) && (
          <Alert severity="info" sx={{ mt: 2 }}>
            La vista previa detallada no está disponible para el método "{metodo}". El nuevo plan se calculará al confirmar.
          </Alert>
        )}
        {previewNuevasCuotas.length > 0 && (
          <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 280 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell align="right">Capital</TableCell>
                  <TableCell align="right">Interés</TableCell>
                  <TableCell align="right">Cuota</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {previewNuevasCuotas.map((c) => (
                  <TableRow key={c.numero_cuota}>
                    <TableCell>{c.numero_cuota}</TableCell>
                    <TableCell>{c.fecha}</TableCell>
                    <TableCell align="right">{c.capital}</TableCell>
                    <TableCell align="right">{c.interes}</TableCell>
                    <TableCell align="right">{c.cuota}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Abono a Capital</DialogTitle>
        <DialogContent dividers>
          {!prestamoDetalle && (
            <Alert severity="warning">Seleccione un préstamo</Alert>
          )}
          {prestamoDetalle && (
            <>
              {renderResumen()}
              <Divider sx={{ my: 2 }} />
              <Box display="flex" gap={2} flexWrap="wrap">
                <TextField
                  label="Monto a abonar"
                  type="number"
                  value={monto}
                  onChange={(e) => setMonto(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">{moneda}</InputAdornment>,
                  }}
                  sx={{ minWidth: 220 }}
                  autoFocus
                />
                <FormControl>
                  <FormLabel>Estrategia</FormLabel>
                  <RadioGroup
                    row
                    value={estrategia}
                    onChange={(e) => setEstrategia(e.target.value)}
                  >
                    <FormControlLabel
                      value="reducir_plazo"
                      control={<Radio />}
                      label="Reducir plazo (mantiene la cuota)"
                    />
                    <FormControlLabel
                      value="reducir_cuota"
                      control={<Radio />}
                      label="Reducir cuota (mantiene el plazo)"
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
              {renderPreview()}
              {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleContinuar}
            disabled={submitting || !prestamoDetalle}
          >
            {submitting ? <CircularProgress size={20} /> : 'Continuar al cobro'}
          </Button>
        </DialogActions>
      </Dialog>

      <MetodoDePagoDialog
        open={showMetodoDialog}
        onClose={() => setShowMetodoDialog(false)}
        onConfirm={handleConfirmMetodoPago}
        amount={montoNum}
        title="Cobro de Abono a Capital"
        description={`Seleccione el método de pago para el abono de ${moneda} ${formatNumberWithCommas(montoNum.toFixed(2))}`}
        showAmount={true}
        allowMultiple={true}
        defaultMethod="efectivo"
      />
    </>
  );
};

export default AbonoCapitalDialog;
